import { PreparedTransaction } from 'thirdweb'
import useSendBatchTxs from './useSendBatchTxs'
import useSendTx from './useSendTx'
import { TransactionReceipt } from 'thirdweb/dist/types/transaction/types'
import isArray from 'lodash/isArray'
import { useState } from 'react'

export interface TransactionResult {
  hash?: string
  logs?: TransactionReceipt['logs']
}

export interface UseExecuteTxProps {
  transaction?:
    | PreparedTransaction
    | PreparedTransaction[]
    | (() => PreparedTransaction | PreparedTransaction[] | undefined)
  onError?: () => void
  onSuccess?: (result: TransactionResult) => void
}

export type TxStatus = 'pending' | 'loading' | 'success' | 'error'

const useExecuteTx = ({
  transaction,
  onError,
  onSuccess,
}: UseExecuteTxProps) => {
  const { sendTx, isPending: isPendingSendTx } = useSendTx()
  const { sendBatchTxs, status } = useSendBatchTxs()
  const [txStatus, setTxStatus] = useState<TxStatus>('pending')
  const isPending = isPendingSendTx || status === 'loading'

  const executeTx = async () => {
    const tx = typeof transaction === 'function' ? transaction() : transaction
    if (!tx) return

    setTxStatus('loading')

    // Handle single or batch transaction
    if (isArray(tx)) {
      const receipt = await sendBatchTxs(tx)
      if (receipt?.status === 'success') {
        onSuccess?.({ hash: receipt.transactionHash, logs: receipt.logs })
        setTxStatus('success')
      } else {
        onError?.()
        setTxStatus('error')
      }
    } else {
      const receipt = await sendBatchTxs([tx])
      if (receipt?.status === 'success') {
        onSuccess?.({ hash: receipt.transactionHash, logs: receipt.logs })
        setTxStatus('success')
      } else {
        onError?.()
        setTxStatus('error')
      }
    }
  }

  return {
    executeTx,
    txStatus,
    isPending,
  }
}

export default useExecuteTx
